import { useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { getRandomAvatarUrl } from '@components/design-avatar';
import { AVATAR_GROUPS } from '@components/design-avatar/avatars';
import { useConfirmModal } from '@components/generic-modals';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconPlay } from '@components/icons/play';
import { InfoBox } from '@components/info-box';
import { JsonValue } from '@components/json-editor';
import { forceHideSidebar, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useAIAgent, useHiddenModels } from 'api/ai-agents/hooks';
import { createJourneyActionFieldPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useWebSocket } from 'api/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { AIModel, AIReasoningEffort, JourneyActionDetailed, VariableValue } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  DEFAULT_MODEL,
  getReasoningEffortConfig,
  normalizeReasoningEffortForModel,
} from 'ui/authenticated/main/pages/ai-agents/ai-agent-config';
import { useModelSelectorModal } from 'ui/authenticated/main/pages/ai-agents/modal-selector-modal';
import { AIModelSelector } from 'ui/authenticated/main/pages/ai-agents/model-selector';
import { Property } from 'ui/authenticated/main/pages/ai-agents/shared/output-list';
import {
  AIAgentConfigurationFields,
  AIAgentKnowledgeFilesField,
  AIAgentOutputFields,
  AIAgentPromptField,
  AIAgentToolsFields,
  fileUrlsToUploadedFiles,
  UploadedFile,
} from 'ui/authenticated/main/pages/ai-agents/shared/sidebar-form-sections';
import { useFeature } from 'ui/hooks';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  createActionForm,
  DescriptionFormField,
  SidebarProps,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { useAIAgentTestModal } from './test-modal';

import { useOutputModal } from '../../authenticated/main/pages/ai-agents/output-modal';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.ask_ai_agent;

export type FormData = {
  name?: string;
  picture?: string;
  description: string;
  prompt: VariableValue;
  additionalFiles: Array<VariableValue>;
  files: UploadedFile[];
  reasoningEffort?: AIReasoningEffort;
  systemInstructions: string;
  model: AIModel;
  jsonSchema?: JsonValue;
  output: Property[];
  useWebSearch: boolean;
  useFileSearch: boolean;
};

const Form = createActionForm<FormData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { t } = useScopedTranslate(sidebarLocale);
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);

  if (isLoading || !data) {
    return (
      <ActionSidebarLoading
        title={t('title')}
        icon={IconAiAgent}
        customIcon={<IconAiAgent variant="gradient" />}
        {...props}
      />
    );
  }

  return (
    <Sidebar>
      <ActionHeader title={t('title')} customIcon={<IconAiAgent variant="gradient" />} {...props} />
      <Sidebar.Content>
        <AnimatePresence initial={false} exitBeforeEnter>
          <Slide key="form" offset={-30}>
            <FormContainer journeyActionId={journeyActionId} data={data} />
          </Slide>
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

const FormContainer = ({
  journeyActionId,
  data,
}: {
  journeyActionId: string;
  data: JourneyActionDetailed<'ask_ai_agent'>;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { isEnabled: hasContentAIEnabled } = useFeature('FEATURE_AI_CONTENT');
  const formStore = useCreateActionFormStore();
  const [initialPicture] = useState(() => data.values?.picture ?? getRandomAvatarUrl());
  const defaultOutput = data.values?.output ?? [];
  const currentOutput = formStore.useFieldValue('output') as Property[] | undefined;
  const output = currentOutput ?? defaultOutput;
  const model = formStore.useFieldValue('model') as AIModel;
  const { data: hiddenModelsData } = useHiddenModels();
  const hiddenModels = hiddenModelsData?.models ?? [];
  const resolvedModel = model ?? data.values?.model ?? DEFAULT_MODEL;
  const reasoningEffortConfig = resolvedModel ? getReasoningEffortConfig(resolvedModel) : undefined;
  const formDescription = formStore.useFieldValue('description');
  const showConfirmModal = useConfirmModal();
  const defaultKnowledgeFiles = fileUrlsToUploadedFiles(data.values?.files);
  const additionalFiles =
    (formStore.useFieldValue('additionalFiles') as Array<VariableValue> | undefined) ??
    data.values?.additionalFiles ??
    [];
  const variables =
    ((formStore.useFieldValue('prompt') as VariableValue) ?? data.values?.prompt ?? {})[
      'variables'
    ] ?? {};
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyAction(journeyActionId, { onSuccess: () => false });
  const showOutputModal = useOutputModal();
  const showModelSelectorModal = useModelSelectorModal();
  const showTestModal = useAIAgentTestModal();

  const handleOutputModal = (index?: number) => {
    showOutputModal({
      setValue: (value) => formStore.actions.setValue('output', value),
      getValue: () => formStore.getState().values['output'] as Property[],
      currentIndex: index,
    });
  };

  const handleSave = async (formData: FormData, shouldHide: boolean = true) => {
    const resolvedAdditionalFiles = formData.additionalFiles ?? additionalFiles;
    const resolvedKnowledgeFiles = formData.files ?? defaultKnowledgeFiles;
    const newValues = {
      ...data.values,
      ...formData,
      picture: formData.picture ?? initialPicture,
      files: resolvedKnowledgeFiles.map((file) => file.url),
      additionalFiles: resolvedAdditionalFiles,
      output: formData.output?.length ? formData.output : output,
    };
    await editMutation.mutateAsync({
      description: formData.description,
      isAutomatic: true,
      action: ACTION_TYPE,
      values: { ...newValues, jsonSchema: newValues.jsonSchema ?? undefined },
    });
    formStore.actions.markAsClean();
    if (shouldHide) {
      forceHideSidebar();
    }
  };

  const variableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    excludedTypes: ['file', 'file_multi'],
  });
  const fileGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['file', 'file_multi'],
  });

  const handleDeleteOutput = (index: number) => {
    const state = formStore.getState();
    const currentOutput = (state.values['output'] ?? []) as Property[];
    formStore.actions.setValue(
      'output',
      currentOutput.filter((_, i) => i !== index),
    );
  };

  const handleSelectModel = (nextModel: AIModel) => {
    formStore.actions.setValue('model', nextModel);

    const currentReasoningEffort =
      (formStore.getState().values['reasoningEffort'] as AIReasoningEffort | undefined) ??
      data.values?.reasoningEffort ??
      null;
    const normalizedReasoningEffort = normalizeReasoningEffortForModel(
      nextModel,
      currentReasoningEffort,
    );

    if (normalizedReasoningEffort) {
      formStore.actions.setValue('reasoningEffort', normalizedReasoningEffort);
    }
  };

  const handleOpenModelSelector = () => {
    showModelSelectorModal({
      currentModel: resolvedModel,
      hiddenModels,
      onSelectModel: handleSelectModel,
    });
  };

  const uploadMutation = useUploadFile(
    createJourneyActionFieldPath(data?.journey ?? '', journeyActionId),
    true,
  );
  const pictureUploadMutation = useUploadFile(
    createJourneyActionFieldPath(data?.journey ?? '', journeyActionId),
    false,
  );

  // Fetch template to check vector store status
  const aiAgentId = data.values?.aiAgentId ?? '';
  const hasTemplateFiles = Boolean(data.values?.files && data.values.files.length > 0);
  const { data: aiAgentTemplate } = useAIAgent(aiAgentId, {
    enabled: Boolean(hasTemplateFiles),
  });
  const isVectorStoreIndexing = aiAgentTemplate?.vectorStoreStatus === 'indexing';
  const isVectorStoreFailed = aiAgentTemplate?.vectorStoreStatus === 'failed';

  const handleShowTestModal = () => {
    showTestModal({
      actionId: journeyActionId,
      variables,
      additionalFiles,
      outputSettings: output,
      onFileSelect: uploadMutation.mutateAsync,
    });
  };

  const handleTestClick = () => {
    if (isDirty) {
      showConfirmModal({
        title: t('unsavedChanges'),
        description: t('unsavedChangesDescription'),
        onConfirm: async () => {
          await handleSave(formStore.getState().values as FormData, false);
          setTimeout(handleShowTestModal);
        },
        buttonText: t('core.save'),
      });
    } else {
      handleShowTestModal();
    }
  };
  const hasOutputError = formStore.useFormErrors().output;
  const currentSystemInstructions = formStore.useFieldValue('systemInstructions') as
    | string
    | undefined;
  const resolvedSystemInstructions =
    currentSystemInstructions ?? data.values?.systemInstructions ?? '';
  const isGenerateOutputDisabled = resolvedSystemInstructions.trim().length === 0;
  const actionDescription = formDescription || data.description;
  const fieldMappingId = getFieldMappingId(data.journey ?? '');
  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack space={6}>
        {isVectorStoreFailed && (
          <InfoBox
            variant="red"
            icon={<IconFormIndicatorWarning />}
            text={t('vectorStoreFailedWarning')}
          />
        )}
        <HideContainer title={t('agentDetails')}>
          <Stack space={4}>
            <Form.DesignAvatarChooserField
              id="picture"
              label={t('agentImage')}
              avatars={AVATAR_GROUPS.robots}
              onSelect={pictureUploadMutation.mutateAsync}
              defaultImage={initialPicture}
            />
            <Form.TextField
              id="name"
              validators={['required']}
              label={t('agentName')}
              defaultValue={data.values?.name}
            />
            <DescriptionFormField defaultValue={data.description} />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('configuration')}>
          <Stack space={4}>
            <AIAgentConfigurationFields
              Form={Form}
              defaultModel={data.values?.model ?? DEFAULT_MODEL}
              resolvedModel={resolvedModel}
              defaultSystemInstructions={data.values?.systemInstructions}
              defaultReasoningEffort={data.values?.reasoningEffort}
              reasoningEffortConfig={reasoningEffortConfig}
              onOpenModelSelector={handleOpenModelSelector}
              renderModelSelector={({ model, onOpen }) => (
                <AIModelSelector model={model as AIModel} onOpen={onOpen} />
              )}
            />
            <AIAgentKnowledgeFilesField
              Form={Form}
              isTemplate={false}
              fieldId="files"
              defaultFiles={defaultKnowledgeFiles}
              onSelect={uploadMutation.mutateAsync}
              additionalFilesConfig={{
                fieldId: 'additionalFiles',
                variableGroups: fileGroups,
                defaultValue: data.values?.additionalFiles,
                fieldMappingId,
              }}
            />
            <HideContainer title={t('tools')}>
              <AIAgentToolsFields
                Form={Form}
                model={resolvedModel}
                defaultUseWebSearch={data.values?.useWebSearch}
                defaultUseFileSearch={data.values?.useFileSearch}
              />
            </HideContainer>
            <HideContainer title={t('input')}>
              <AIAgentPromptField
                Form={Form}
                isTemplate={false}
                variableGroups={variableGroups}
                defaultValue={data.values?.prompt}
                fieldMappingId={fieldMappingId}
                aiOptions={
                  hasContentAIEnabled
                    ? {
                        generateChoices: actionDescription
                          ? [
                              {
                                title: t('generateFromActionDescription'),
                                prompt: `Write an AI agent prompt with this description: ${actionDescription}`,
                              },
                            ]
                          : [],
                        threadInstructions:
                          'Transform the users input into a refined prompt intended for another AI agent. Do not generate any output beyond the prompt itself—do not write the actual story, joke, or content. Only return the improved prompt. For instance, if the user says "write a story about a robot", the refined prompt should be: "You are a creative storyteller. Write a captivating short story about a curious robot exploring a futuristic world."',
                        useWebSocket,
                      }
                    : undefined
                }
              />
            </HideContainer>
            <HideContainer title={t('output')}>
              <AIAgentOutputFields
                Form={Form}
                output={output}
                defaultOutput={defaultOutput}
                systemInstructions={resolvedSystemInstructions}
                setOutput={(value) => formStore.actions.setValue('output', value)}
                handleOutputModal={handleOutputModal}
                handleDeleteOutput={handleDeleteOutput}
                hasOutputError={hasOutputError}
                isGenerateOutputDisabled={isGenerateOutputDisabled}
              />
            </HideContainer>
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
      </Stack>
      <ActionFooter action={data} isDisabled={!isDirty || editMutation.isLoading} form={Form}>
        <Button
          variant="ghost"
          iconLeft={<IconPlay />}
          text={t('test')}
          onClick={handleTestClick}
          isDisabled={isVectorStoreIndexing}
          tooltip={isVectorStoreIndexing ? t('testDisabledIndexing') : undefined}
        />
      </ActionFooter>
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    title: 'AI Agent',
    agentDetails: 'Agent details',
    agentImage: 'Agent image',
    agentName: 'Agent name',
    configuration: 'Configuration',
    tools: 'Tools',
    systemInstructions: 'System instructions',
    input: 'Input',
    output: 'Output',
    prompt: 'Prompt',
    test: 'Test',
    testDisabledIndexing: 'Cannot test while knowledge base is indexing',
    vectorStoreFailedWarning:
      'Knowledge base indexing failed. Please re-save the AI Agent template to retry.',
    unsavedChanges: 'Unsaved changes',
    unsavedChangesDescription: 'Before testing, please save your changes',
    generateFromActionDescription: 'Generate from action description',
  },
};
